/**
 * Good Samaritan coloring pages — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_bible_stories`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const goodSamaritanColoringPagesPack: ToilePack = {
    slug: 'good-samaritan-coloring-pages',
    category: 'faith',
    name: 'Good Samaritan coloring pages',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'roadside', label: 'On the road', subject: 'a hurt traveler lying by the rocky road to Jericho as a priest passes by on the other side'},
        {key: 'help', label: 'Helping', subject: "the Samaritan kneeling to bandage the man's wounds with oil and wine, his donkey waiting beside them"},
        {key: 'inn', label: 'At the inn', subject: 'the Samaritan paying the innkeeper two coins at the inn door while the man rests inside'},
    ],
    styles: [
        {key: 'bold', label: 'Bold and simple', style: 'bold thick-outline coloring book style, one heavy uniform black line, big simple open shapes for young children', palette: 'black ink only', framing: 'The drawing centered with a wide white margin on all sides'},
        {key: 'classic', label: 'Classic', style: 'classic coloring book line art, medium uniform black line, moderate detail and open shapes for ages six to nine', palette: 'black ink only', framing: 'The full page used, the drawing centered with a white margin'},
        {key: 'detailed', label: 'Fine detail', style: 'fine-line detailed coloring page, thin uniform black line, patterned robes, leaves and waves for older children', palette: 'black ink only', framing: 'The scene held inside a plain thin rectangular border with a white margin outside it'},
    ],
    directions: ['the donkey', 'the inn door', 'road at dawn'],
    directionHint: 'try: the donkey',
    seo: {
        title: 'Good Samaritan Coloring Pages: Luke 10 in Three Scenes',
        metaDescription: "Nine Good Samaritan coloring pages for Sunday school from Luke 10: the traveler by the road, the Samaritan's help and the inn, in three styles.",
        h1: 'Good Samaritan coloring pages: on the road, helping, at the inn',
        tagline: 'Nine pages of the parable of the good Samaritan, each in three line weights.',
        intro: 'Printable Good Samaritan coloring pages for Sunday school and Christian homeschool, the parable in Luke 10. Three scenes: a hurt traveler by the road to Jericho as a priest passes by, the Samaritan bandaging his wounds beside his donkey, and the Samaritan paying the innkeeper at the door. Bold, classic and fine-detail lines. Type a different scene or detail, and the same styles carry it.',
        useCases: [
            'a Sunday school lesson on Luke 10',
            "a children's church craft activity",
            'a Christian homeschool Gospel unit',
            'a bulletin board on loving your neighbor',
        ],
        faq: [
            {q: 'Which part of the parable do these show?', a: "The traveler on the road, the Samaritan's help, and the inn."},
            {q: 'Can I ask for the donkey or the inn door?', a: 'Yes. Type the detail you want. The request shapes the page, but a result is not promised to match it exactly.'},
            {q: 'Which style suits a young child?', a: 'Bold and simple, with one heavy line and big open shapes.'},
            {q: 'Are the pages in color?', a: 'No. Each page is black line on white, with open shapes to color in.'},
        ],
    },
}
